import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Box, Flex } from 'components/Layout/Layout';

import Input from 'components/Input/Input';
import LoginView from 'components/LoginView/LoginView';
import Button from 'components/Button/Button';
import P from 'components/P/P';

import messages from './messages';
import { useStores } from '../../store/useStores';

const VALID_PASSWORD = 'isValid';
const ERROR_PASSWORD_NOT_EQUAL = 'notEqual';
const ERROR_PASSWORD_EMPTY = 'empty';
const ERROR_PASSWORD_TOO_SHORT = 'tooShort';
const ERROR_PASSWORD_NO_ALPHABETICAL_CHAR = 'noAlphabeticalChar';
const ERROR_PASSWORD_NO_NUMERICAL_CHAR = 'noNumericalChar';

export const isPasswordValid = password => {
  if (password) {
    if (password.length >= 8) {
      if (password.match(/[a-z]/i)) {
        if (password.match(/[0-9]/i)) {
          return VALID_PASSWORD;
        }
        return ERROR_PASSWORD_NO_NUMERICAL_CHAR;
      }
      return ERROR_PASSWORD_NO_ALPHABETICAL_CHAR;
    }
    return ERROR_PASSWORD_TOO_SHORT;
  }
  return ERROR_PASSWORD_EMPTY;
};

function SetPasswordView() {
  const intl = useIntl();
  const { authStore } = useStores();

  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [password1Error, setPassword1Error] = useState(ERROR_PASSWORD_EMPTY);
  const [password2Error, setPassword2Error] = useState(ERROR_PASSWORD_EMPTY);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.add('blue');
    return () => {
      document.body.classList.remove('blue');
    };
  }, []);

  const getMessageForError = useCallback(errCode => {
    switch (errCode) {
      case VALID_PASSWORD:
        return messages.validPassword;
      case ERROR_PASSWORD_EMPTY:
        return messages.errorPasswordEmpty;
      case ERROR_PASSWORD_TOO_SHORT:
        return messages.errorPasswordTooShort;
      case ERROR_PASSWORD_NOT_EQUAL:
        return messages.errorPasswordNotEqual;
      case ERROR_PASSWORD_NO_ALPHABETICAL_CHAR:
        return messages.errorPasswordNoAlphabeticalChar;
      case ERROR_PASSWORD_NO_NUMERICAL_CHAR:
        return messages.errorPasswordNoNumericalChar;
      default:
        return messages.errorSomethingWentWrong;
    }
  }, []);

  const onBlur1 = useCallback(() => {
    const validState =
      isPasswordValid(password1) === VALID_PASSWORD && password2 === password1;
    if (!validState) {
      setPassword1Error(
        isPasswordValid(password1) !== VALID_PASSWORD
          ? isPasswordValid(password1)
          : password2 && password1 && password2 !== password1
          ? ERROR_PASSWORD_NOT_EQUAL
          : VALID_PASSWORD
      );
    } else {
      setPassword1Error(VALID_PASSWORD);
      setPassword2Error(VALID_PASSWORD);
    }
  }, [password1, password2]);

  const onBlur2 = useCallback(() => {
    const validState =
      isPasswordValid(password2) === VALID_PASSWORD && password2 === password1;
    if (!validState) {
      setPassword2Error(
        isPasswordValid(password2) !== VALID_PASSWORD
          ? isPasswordValid(password2)
          : password2 && password1 && password2 !== password1
          ? ERROR_PASSWORD_NOT_EQUAL
          : VALID_PASSWORD
      );
    } else {
      setPassword1Error(VALID_PASSWORD);
      setPassword2Error(VALID_PASSWORD);
    }
  }, [password1, password2]);

  const updatePassword1 = useCallback(
    e => {
      const value = e.target.value;
      const validState =
        isPasswordValid(password2) === VALID_PASSWORD && password2 === value;
      setPassword1(value);
      setPassword1Error(validState ? VALID_PASSWORD : ERROR_PASSWORD_EMPTY);
    },
    [password2]
  );

  const updatePassword2 = useCallback(
    e => {
      const value = e.target.value;
      const validState =
        isPasswordValid(password1) === VALID_PASSWORD && password1 === value;
      setPassword2(value);
      setPassword2Error(validState ? VALID_PASSWORD : ERROR_PASSWORD_EMPTY);
    },
    [password1]
  );

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      if (
        isPasswordValid(password1) === VALID_PASSWORD &&
        password1 === password2
      ) {
        setError(null);
        const response = await authStore.setPassword(password1);
        if (response?.error) {
          setError(response.error.message);
        }
        return;
      }

      setPassword1Error(
        password1 !== password2
          ? ERROR_PASSWORD_NOT_EQUAL
          : isPasswordValid(password1)
      );
      setPassword2Error(
        password1 !== password2
          ? ERROR_PASSWORD_NOT_EQUAL
          : isPasswordValid(password2)
      );
      setError(null);
    },
    [authStore, password1, password2]
  );

  const title = intl.formatMessage(messages.loginTitle);
  const message = intl.formatMessage(messages.setPasswordMessage);

  const border1 = useMemo(
    () =>
      password1Error === VALID_PASSWORD
        ? false
        : ERROR_PASSWORD_EMPTY !== password1Error,
    [password1Error]
  );

  const border2 = useMemo(
    () =>
      password2Error === VALID_PASSWORD
        ? false
        : ERROR_PASSWORD_EMPTY !== password2Error,
    [password2Error]
  );

  return (
    <LoginView title={title} message={message}>
      <form onSubmit={handleSubmit}>
        {error && (
          <Box mb={10}>
            <P danger>{error}</P>
          </Box>
        )}
        <Box mb={3}>
          <Input
            onBlur={onBlur1}
            placeholder={intl.formatMessage(messages.loginPassword)}
            onChange={updatePassword1}
            value={password1}
            type="password"
            error={border1}
          />
        </Box>
        {password1Error !== VALID_PASSWORD &&
          password1Error !== ERROR_PASSWORD_EMPTY &&
          password1Error !== ERROR_PASSWORD_NOT_EQUAL && (
            <Box mb={10}>
              <P danger>
                {intl.formatMessage(getMessageForError(password1Error))}
              </P>
            </Box>
          )}
        <Box mb={3}>
          <Input
            onBlur={onBlur2}
            onChange={updatePassword2}
            placeholder={intl.formatMessage(messages.loginPasswordRepeat)}
            value={password2}
            type="password"
            error={border2}
          />
        </Box>
        {password2Error !== VALID_PASSWORD &&
          password2Error !== ERROR_PASSWORD_EMPTY &&
          password2Error !== ERROR_PASSWORD_NOT_EQUAL && (
            <Box mb={10}>
              <P danger>
                {intl.formatMessage(getMessageForError(password2Error))}
              </P>
            </Box>
          )}
        {(password1Error === ERROR_PASSWORD_NOT_EQUAL ||
          password2Error === ERROR_PASSWORD_NOT_EQUAL) && (
          <Box mb={10}>
            <P danger>
              <FormattedMessage {...messages.errorPasswordNotEqual} />{' '}
            </P>
          </Box>
        )}
        <Flex flexWrap="wrap">
          <Box width={[1, 1, 1 / 2]} mb={[3, 3, 0]}>
            <Button
              disabled={!password1 || !password2 || password1 !== password2}
              secondary
            >
              <FormattedMessage {...messages.setPasswordSubmitButton} />
            </Button>
          </Box>
        </Flex>
      </form>
    </LoginView>
  );
}

export default observer(SetPasswordView);
